// ./hooks/useCourse.js

import { useContext, useCallback } from 'react';
import { GlobalContext } from '../context/provider';
import courseAPI from '../api/courseAPI';

const useCourse = () => {
  const { 
      courseState,
      courseDispatch
  } = useContext(GlobalContext);
  
  // ============================================================================
  // COURSE MANAGEMENT
  // ============================================================================

  const getAllCourses = useCallback(async (params = {}) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.getAllCourses(params);
      courseDispatch({ type: 'SET_COURSES', payload: response.data });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const getPublishedCourses = useCallback(async () => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.getPublishedCourses();
      courseDispatch({ type: 'SET_PUBLISHED_COURSES', payload: response.data });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const searchCourses = useCallback(async (query) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.searchCourses(query);
      courseDispatch({ type: 'SET_SEARCH_RESULTS', payload: response.data });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const createCourse = useCallback(async (courseId, courseData) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.createCourse(courseId, courseData);
      courseDispatch({ type: 'ADD_COURSE', payload: response.data });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const getCourseById = useCallback(async (courseId) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.getCourseById(courseId);
      courseDispatch({ type: 'SET_CURRENT_COURSE', payload: response.data });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const updateCourse = useCallback(async (courseId, updateData) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.updateCourse(courseId, updateData);
      courseDispatch({ type: 'UPDATE_COURSE', payload: response.data });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const deleteCourse = useCallback(async (courseId) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.deleteCourse(courseId);
      courseDispatch({ type: 'DELETE_COURSE', payload: courseId });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const publishCourse = useCallback(async (courseId, publishedById) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.publishCourse(courseId, publishedById);
      courseDispatch({ type: 'PUBLISH_COURSE', payload: courseId });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const unpublishCourse = useCallback(async (courseId, unpublishedById) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.unpublishCourse(courseId, unpublishedById);
      courseDispatch({ type: 'UNPUBLISH_COURSE', payload: courseId });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  // ============================================================================
  // LESSON MANAGEMENT
  // ============================================================================

  const addLesson = useCallback(async (courseId, lessonId, lessonData) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.addLesson(courseId, lessonId, lessonData);
      courseDispatch({ type: 'ADD_LESSON', payload: response.data });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const removeLesson = useCallback(async (courseId, lessonId) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.removeLesson(courseId, lessonId);
      courseDispatch({ type: 'DELETE_LESSON', payload: lessonId });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  // ============================================================================
  // LAB MANAGEMENT
  // ============================================================================

  const addLab = useCallback(async (courseId, labId, labData) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.addLab(courseId, labId, labData);
      courseDispatch({ type: 'ADD_LAB', payload: response.data });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const removeLab = useCallback(async (courseId, labId) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.removeLab(courseId, labId);
      courseDispatch({ type: 'DELETE_LAB', payload: labId });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  // ============================================================================
  // QUIZ MANAGEMENT
  // ============================================================================

  const addQuiz = useCallback(async (courseId, quizId, quizData) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.addQuiz(courseId, quizId, quizData);
      courseDispatch({ type: 'ADD_QUIZ', payload: response.data });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const removeQuiz = useCallback(async (courseId, quizId) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.removeQuiz(courseId, quizId);
      courseDispatch({ type: 'DELETE_QUIZ', payload: quizId });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  // ============================================================================
  // EXAM MANAGEMENT
  // ============================================================================

  const addExam = useCallback(async (courseId, examId, examData) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.addExam(courseId, examId, examData);
      courseDispatch({ type: 'ADD_EXAM', payload: response.data });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const removeExam = useCallback(async (courseId, examId) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.removeExam(courseId, examId);
      courseDispatch({ type: 'DELETE_EXAM', payload: examId });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

    // ============================================================================
  // CAPSTONE MANAGEMENT
  // ============================================================================

  const addCapstone = useCallback(async (courseId, capstoneId, capstoneData) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.addCapstone(courseId, capstoneId, capstoneData);
      courseDispatch({ type: 'ADD_CAPSTONE', payload: response.data });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const removeCapstone = useCallback(async (courseId, capstoneId) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.removeCapstone(courseId, capstoneId);
      courseDispatch({ type: 'DELETE_CAPSTONE', payload: capstoneId });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  // ============================================================================
  // UPDATE COURSE CONTENT (Unified)
  // ============================================================================

  const updateCourseContent = useCallback(async (courseId, contentType, contentId, updateData) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.updateCourseContent(
        courseId, 
        contentType, 
        contentId, 
        updateData
      );
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, error: error.message };
    } finally {
      courseDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [courseDispatch]);

   // ============================================================================
  // INSTRUCTOR MANAGEMENT
  // ============================================================================

  const updateCourseInstructor = useCallback(async (courseId, instructorData) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.updateCourseInstructor(courseId, instructorData);
      
      if (response.success) {
        courseDispatch({ type: 'UPDATE_COURSE', payload: response.data });
      }
      
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      courseDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [courseDispatch]);

  const removeCourseInstructor = useCallback(async (courseId, removedBy) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.removeCourseInstructor(courseId, removedBy);
      
      if (response.success) {
        courseDispatch({ type: 'UPDATE_COURSE', payload: response.data });
      }
      
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      courseDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [courseDispatch]);

  const addCategory = useCallback(async (name) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.addCategory(name);
      courseDispatch({ type: 'ADD_CATEGORY', payload: response.data });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const updateCategory = useCallback(async (oldName, newName) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.updateCategory(oldName, newName);
      courseDispatch({ type: 'UPDATE_CATEGORY', payload: { oldName, newName } });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const deleteCategory = useCallback(async (name) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.deleteCategory(name);
      courseDispatch({ type: 'DELETE_CATEGORY', payload: name });
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  const getCoursesByCategory = useCallback(async (name) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.getCoursesByCategory(name);
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  // ============================================================================
  // COUPON MANAGEMENT - ADD THESE TO useCourse.js
  // ============================================================================

  const addCoupon = useCallback(async (courseId, couponData) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.addCoupon(courseId, couponData);
      
      if (response.success) {
        courseDispatch({ type: 'UPDATE_COURSE', payload: response.data });
      }
      
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    } finally {
      courseDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [courseDispatch]);

  const removeCoupon = useCallback(async (courseId, couponCode, removedBy) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.removeCoupon(courseId, couponCode, removedBy);
      
      if (response.success) {
        courseDispatch({ type: 'UPDATE_COURSE', payload: response.data });
      }
      
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    } finally {
      courseDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [courseDispatch]);

  const updateCoupon = useCallback(async (courseId, couponCode, updates, updatedBy) => {
    courseDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await courseAPI.updateCoupon(courseId, couponCode, updates, updatedBy);
      
      if (response.success) {
        courseDispatch({ type: 'UPDATE_COURSE', payload: response.data });
      }
      
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, message: error.message };
    } finally {
      courseDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [courseDispatch]);

  const validateCoupon = useCallback(async (courseId, couponCode) => {
    try {
      const response = await courseAPI.validateCoupon(courseId, couponCode);
      return response;
    } catch (error) {
      return { success: false, message: error.message };
    }
  }, []);

  const calculateDiscountedPrice = useCallback(async (courseId, couponCode) => {
    try {
      const response = await courseAPI.calculateDiscountedPrice(courseId, couponCode);
      return response;
    } catch (error) {
      return { success: false, message: error.message };
    }
  }, []);

  const getCourseDomains = useCallback(async () => {
    try {
      const response = await courseAPI.getCourseDomains();
      if (response.success) {
        courseDispatch({ type: 'SET_COURSE_DOMAINS', payload: response.data });
      }
      return response;
    } catch (error) {
      courseDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [courseDispatch]);

  // ============================================================================
  // UTILITY
  // ============================================================================

  const clearCourseData = useCallback(() => {
    courseDispatch({ type: 'CLEAR_COURSE_DATA' });
  }, [courseDispatch]);

  return {
    // courseState
    courseData: courseState.courseData,
    currentCourse: courseState.currentCourse,
    courseLessons: courseState.courseLessons,
    courseLabs: courseState.courseLabs,
    courseQuizzes: courseState.courseQuizzes,
    courseExams: courseState.courseExams,
    courseCapstones: courseState.courseCapstones,
    courseModules: courseState.courseModules,
    enrolledStudents: courseState.enrolledStudents,
    searchResults: courseState.searchResults,
    publishedCourses: courseState.publishedCourses,
    courseAnalytics: courseState.courseAnalytics,
    isLoading: courseState.isLoading,
    error: courseState.error,
    courseDomains: courseState.courseDomains,

    // Methods
    getAllCourses,
    getPublishedCourses,
    searchCourses,
    createCourse,
    getCourseById,
    updateCourse,
    updateCourseInstructor,
    removeCourseInstructor,
    deleteCourse,
    publishCourse,
    unpublishCourse,
    addLesson,
    removeLesson,
    addLab,
    removeLab,
    addQuiz,
    removeQuiz,
    addExam,
    removeExam,
    addCapstone,
    removeCapstone,
    updateCourseContent,
    // getAllCategories,
    addCategory,
    updateCategory,
    deleteCategory,
    getCoursesByCategory,
    getCourseDomains,
    clearCourseData,

    addCoupon,
    removeCoupon,
    updateCoupon,
    validateCoupon,
    calculateDiscountedPrice,
  };
};

export default useCourse;